import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowSquareOut, Check, ClipboardText, Plus, X } from 'shared/icons';
import {
    CASE_FISCAL_TYPES,
    CaseFiscalDoc,
    ChannelMeta,
    DESTINATION_TAG_KEY,
    HasMlReturnInvoice,
    IsActionTag,
    IsUrgentTag,
    MANUAL_TAG_COLOR_KEYS,
    MANUAL_TAG_COLORS,
    ManualTagColor,
    ORDER_STATUS_META,
    OriginMeta,
    PEDIDO_TAG_META,
    PILL_NEUTRAL,
    ReturnTrackingMethodLabel,
    SECTOR_META,
    SECTOR_ORDER,
    TAG_KIND_META,
    TagDestinationTitleParts,
    TagKind,
    TagLabel,
    TagSupportTitleParts,
    TagWireLabel,
    TONE_CLASSES,
    URGENT_REASONS,
    URGENT_RING_CLASSES,
    WEEKDAYS_ISO,
    WITHDRAWAL_SUBSTATUS,
} from '../../constants/pedidoTags';
import { GetToken } from '../../services/api';
import { ResolveAssetUrl, ResolveUploadsBaseUrl } from '../../utils/assetUrl';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatInt } from '../../utils/format';
import { MLB_URLS } from '../../utils/ml';
import { fmtDate, timeAgo } from '../rma/rmaShared';
import { CaseBlingFiscalAction } from './CaseBlingFiscal';

// ── Fundações visuais da Gestão de Pedidos (2026-09-22, rodada 2 em 09-23) ──
// Mesma linguagem do módulo RMA (`pages/Rmas.jsx` + `rma/rmaShared.jsx`):
// reta e plana (cantos QUADRADOS), negrito SÓ na identidade e na ação
// primária, cor com SIGNIFICADO — a borda esquerda diz de quem é a pendência:
//   red = urgente · sky = Atendimento/ML · amber = Logística/galpão ·
//   cyan = Financeiro · gray = neutro/concluído.
// Formatadores de data (`fmtDate`/`timeAgo`) NÃO são reexportados daqui — os
// consumidores importam direto de `components/rma/rmaShared`.

/**
 * `sales.pack_id` é coluna gerada do JSON: quando o JSON é null a coluna
 * guarda a STRING 'null'. O backend já normaliza, mas a guarda fica aqui
 * também porque a identidade é a manchete da linha — errar aqui é visível.
 */
export function CleanPackId(packId) {
    return CleanIdentityValue(packId);
}

/** Valor de identidade limpo: null/undefined/''/'null'/'undefined' viram null. */
export function CleanIdentityValue(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const text = String(value).trim();
    return text && text !== 'null' && text !== 'undefined' ? text : null;
}

/**
 * Meta resolvida de uma tag automática — aceita a key ou o objeto do wire.
 * `kind` (ação × acompanhamento) vem do wire ou do catálogo; `urgent` vem SÓ
 * do wire (catálogo v3: o front não deriva urgência); `dueAt` = `due_at` (o
 * prazo mais próximo entre ML e SLA); `slaDueAt` = `sla_due_at`;
 * `urgentReason` = `urgent_reason` ('prazo_ml' | 'sla').
 */
function resolveTag(tagKey, tag) {
    const key = tag?.key || tagKey || '';
    const meta = PEDIDO_TAG_META[key];
    const sector = tag?.sector || meta?.sector || null;
    const source = tag && typeof tag === 'object' ? { ...tag, key } : key;
    const kind = TagKind(source);
    const tone = meta?.tone || SECTOR_META[sector]?.tone || 'neutral';
    return {
        key,
        sector,
        kind,
        action: kind === 'acao',
        urgent: IsUrgentTag(source),
        tone,
        label: TagLabel(key),
        origin: tag?.origin || null,
        dueAt: tag?.due_at || null,
        // O wire da rodada 3b manda `due_at` em toda tag (null = sem prazo);
        // sem o campo (backend antigo) não se afirma "sem prazo".
        hasDueField: Boolean(tag && typeof tag === 'object' && 'due_at' in tag),
        slaDueAt: tag?.sla_due_at || null,
        mlDueAt: tag?.ml_due_at || null,
        urgentReason: URGENT_REASONS.includes(tag?.urgent_reason) ? tag.urgent_reason : null,
        slaMinutes: Number(tag?.sla_minutes) > 0 ? Number(tag.sla_minutes) : null,
    };
}

/**
 * Instante → "dd/mm hh:mm" em Brasília — o formatador de instantes do sistema
 * (`FormatDateTimeBr`) sem o ano. Vazio/inválido → ''. Usado no prazo das
 * tags e no "alterado em" das Regras.
 */
export function FormatShortInstant(iso) {
    const text = FormatDateTimeBr(iso, { blank: '' });
    return text ? text.replace(/\/\d{4},?/, '') : '';
}

const MINUTE_MS = 60 * 1000;

/**
 * Minutos → texto exato e curto: 15 → "15 min", 60 → "1 h", 90 → "1 h 30",
 * 1500 → "1 d 1 h". Usado nos SLAs (página de Regras e title do chip).
 * Inválido/≤ 0 → ''.
 */
export function FormatMinutesExact(value) {
    const total = Math.round(Number(value));
    if (!Number.isFinite(total) || total <= 0) {
        return '';
    }
    if (total < 60) {
        return `${total} min`;
    }
    const days = Math.floor(total / 1440);
    const hours = Math.floor((total % 1440) / 60);
    const minutes = total % 60;
    const parts = [];
    if (days) {
        parts.push(`${days} d`);
    }
    if (hours) {
        parts.push(`${hours} h`);
    }
    if (minutes) {
        parts.push(days || hours ? String(minutes).padStart(2, '0') : `${minutes} min`);
    }
    return parts.join(' ');
}

/** Quantidade legível: < 1 h em min, < 48 h em h, senão em dias (sempre para baixo). */
function formatSpan(minutes) {
    if (minutes < 60) {
        return `${Math.max(1, minutes)} min`;
    }
    if (minutes < 48 * 60) {
        return `${Math.floor(minutes / 60)} h`;
    }
    return `${Math.floor(minutes / (24 * 60))} d`;
}

/**
 * Tempo até o prazo: "vence em 3 h" / "vencido há 2 h" (min abaixo de 1 h,
 * dias acima de 48 h). Prazo inválido → ''. `now` injetável (testes).
 */
export function DeadlineDistance(dueAt, now = Date.now()) {
    const due = dueAt ? new Date(dueAt).getTime() : Number.NaN;
    if (!Number.isFinite(due)) {
        return '';
    }
    const minutes = Math.floor(Math.abs(due - now) / MINUTE_MS);
    return due >= now ? `vence em ${formatSpan(minutes)}` : `vencido há ${formatSpan(minutes)}`;
}

/**
 * Texto do PRAZO no `title` do chip de tag (Regras, rodada 3b + adendo SLA):
 *   urgente por SLA      → "Atrasado há 12 min (SLA de 15 min)" (o SLA vem do
 *                          wire `sla_minutes` ou do mapa `slaMinutes` das
 *                          Regras; sem nenhum dos dois, "(SLA)");
 *   urgente por prazo ML → "Prazo do ML: vence em 3 h" / "vencido há 2 h"
 *                          (usa `ml_due_at` quando vier; senão `due_at`, só
 *                          se ele não for o próprio SLA);
 *   não urgente          → "Prazo: dd/mm hh:mm" (e "SLA até dd/mm hh:mm"
 *                          quando o SLA for outro instante); ação com
 *                          `due_at: null` no wire → "sem prazo".
 * Devolve a lista de partes (vazia quando não há o que dizer).
 */
export function TagDeadlineParts(tag, { slaMinutes = null, now = Date.now() } = {}) {
    const resolved = resolveTag(null, tag);
    const { action, urgent, dueAt, hasDueField, slaDueAt, mlDueAt, urgentReason } = resolved;
    const sla = resolved.slaMinutes || (Number(slaMinutes) > 0 ? Number(slaMinutes) : null);
    const slaLabel = sla ? `SLA de ${FormatMinutesExact(sla)}` : 'SLA';
    if (urgent && urgentReason === 'sla' && (slaDueAt || dueAt)) {
        const late = Math.floor(Math.max(0, now - new Date(slaDueAt || dueAt).getTime()) / MINUTE_MS);
        return [`Atrasado há ${formatSpan(late)} (${slaLabel})`];
    }
    if (urgent && urgentReason === 'prazo_ml') {
        const mlDue = mlDueAt || (dueAt && dueAt !== slaDueAt ? dueAt : null);
        return [mlDue ? `Prazo do ML: ${DeadlineDistance(mlDue, now)}` : 'Prazo do ML até o corte da Regra 1'];
    }
    if (urgent && dueAt) {
        return [DeadlineDistance(dueAt, now)];
    }
    const parts = [];
    if (dueAt) {
        parts.push(`Prazo: ${FormatShortInstant(dueAt)}`);
    } else if (action && hasDueField) {
        parts.push('sem prazo');
    }
    if (slaDueAt && slaDueAt !== dueAt) {
        parts.push(`SLA até ${FormatShortInstant(slaDueAt)}`);
    }
    return parts;
}

// Partes do instante no fuso de Brasília (dia da semana em inglês curto só
// para mapear ao ISO — o texto exibido vem de `WEEKDAYS_ISO`).
const SP_PARTS = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
const EN_WEEKDAY_TO_ISO = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

function spParts(date) {
    return Object.fromEntries(SP_PARTS.formatToParts(date).map((part) => [part.type, part.value]));
}

/**
 * Data civil de HOJE no fuso de Brasília, 'AAAA-MM-DD' (teto do campo da data
 * de corte nas Regras — o fuso das regras). `now` injetável (testes).
 */
export function TodaySaoPauloIso(now = Date.now()) {
    const parts = spParts(new Date(now));
    return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Corte da urgência (`summary.urgency_cutoff`, ISO) → "terça 24/09 09:00" no
 * fuso de Brasília, com "(hoje)"/"(amanhã)" quando for o caso. Vazio/inválido
 * → ''. `now` injetável (testes).
 */
export function FormatUrgencyCutoff(iso, now = Date.now()) {
    const date = iso ? new Date(iso) : null;
    if (!date || Number.isNaN(date.getTime())) {
        return '';
    }
    const parts = spParts(date);
    const weekday = WEEKDAYS_ISO.find((day) => day.iso === EN_WEEKDAY_TO_ISO[parts.weekday])?.label || '';
    const text = `${weekday} ${parts.day}/${parts.month} ${parts.hour}:${parts.minute}`.trim();
    const today = spParts(new Date(now));
    const tomorrow = spParts(new Date(now + 24 * 60 * MINUTE_MS));
    const dayKey = (p) => `${p.year}-${p.month}-${p.day}`;
    if (dayKey(parts) === dayKey(today)) {
        return `${text} (hoje)`;
    }
    if (dayKey(parts) === dayKey(tomorrow)) {
        return `${text} (amanhã)`;
    }
    return text;
}

/**
 * Explicação de "Urgentes" (title do chip e do contador vermelho dos blocos) —
 * os DOIS regimes: prazo do ML até o corte da Regra 1 ou SLA interno
 * estourado. Um texto só para os dois lugares não divergirem de novo.
 */
export function UrgentRuleTitle(cutoffIso) {
    const cutoff = FormatUrgencyCutoff(cutoffIso);
    return `Urgentes: ação nossa com prazo do ML até ${cutoff || 'o corte da Regra 1'} (Regra 1) ou que passou do SLA de resposta`;
}

/**
 * Tags de AÇÃO primeiro (a vez é nossa), depois acompanhamento, preservando
 * a ordem do wire dentro de cada grupo. Aceita objetos do wire ou keys.
 */
export function SortTagsActionFirst(tags = []) {
    const list = Array.isArray(tags) ? tags : [];
    const rank = (tag) => (IsActionTag(tag) ? 0 : 1);
    return list
        .map((tag, index) => ({ tag, index }))
        .sort((a, b) => rank(a.tag) - rank(b.tag) || a.index - b.index)
        .map(({ tag }) => tag);
}

// ── Chips ────────────────────────────────────────────────────────────────────

/**
 * Selo DISCRETO de origem (`ml` / `sistema` / `bling`) — entra no chip e nos
 * blocos quando o wire trouxer `origin`; sem origem, não renderiza nada.
 */
export const OriginBadge = ({ origin }) => {
    const meta = OriginMeta(origin);
    if (!meta) {
        return null;
    }
    return (
        <span
            title={meta.title}
            className={`ml-0.5 px-1 text-[9px] font-medium uppercase tracking-wider ${meta.badge}`}
        >
            {meta.label}
        </span>
    );
};

// ── Retirada da devolução na agência (rodada 7) ─────────────────────────────

/** Tag de ação da Logística: devolução parada na agência esperando a nossa retirada. */
export const WITHDRAWAL_TAG_KEY = 'devolucao_para_retirar';

/**
 * Partes do `title` da RETIRADA: método (PAC = Correios), rastreio e "na
 * agência desde dd/mm hh:mm" (entrada no substatus waiting_for_withdrawal,
 * Brasília). Recebe o `detail` do chip no wire — `{ tracking_method,
 * tracking_number, since }` (BuildTagWire, coluna `det_<key>`); sem ele,
 * nada.
 */
export function WithdrawalTitleParts(detail) {
    if (!detail || typeof detail !== 'object') {
        return [];
    }
    const method = ReturnTrackingMethodLabel(detail.tracking_method);
    const tracking = CleanIdentityValue(detail.tracking_number);
    const since = FormatShortInstant(detail.since);
    return [method, tracking ? `rastreio ${tracking}` : null, since ? `na agência desde ${since}` : null].filter(
        Boolean,
    );
}

/**
 * `detail` da retirada a partir dos casos do pedido (detalhe): o caso cuja
 * devolução está parada na agência (`return_shipment_substatus` =
 * waiting_for_withdrawal), no MESMO formato do `detail` do chip — usado só
 * quando o chip do wire vier sem `detail`. Nenhum → null.
 */
export function WithdrawalCase(ocorrencias = []) {
    const list = Array.isArray(ocorrencias) ? ocorrencias : [];
    const occ = list.find((item) => item?.return_shipment_substatus === WITHDRAWAL_SUBSTATUS);
    if (!occ) {
        return null;
    }
    return {
        tracking_method: occ.return_tracking_method ?? null,
        tracking_number: occ.tracking_number ?? null,
        since: occ.return_shipment_substatus_at ?? null,
    };
}

/**
 * Chip de tag AUTOMÁTICA: cor do setor; URGENTE (`urgent` do wire) ganha
 * contorno red e um quadradinho red antes do rótulo (lê-se mesmo em
 * impressão P&B). O `title` diz setor · tipo (para fazer / acompanhar) ·
 * urgente · prazo (`TagDeadlineParts`: "Prazo: dd/mm hh:mm", "sem prazo",
 * "Atrasado há 12 min (SLA de 15 min)", "Prazo do ML: vence em 3 h").
 * `slaMinutes` = SLA da tag pelas Regras (quando o wire não traz
 * `sla_minutes`). Com `origin` no wire, mostra o selo de origem.
 * Retirada (rodada 7): o chip `devolucao_para_retirar` acrescenta método,
 * rastreio e "na agência desde …" (`WithdrawalTitleParts`) — do `detail` do
 * chip no wire ou, sem ele, de `info` (mesmo formato, montado do caso do
 * pedido no detalhe — `WithdrawalCase`).
 * Destino (rodada 8): `devolucao_para_destinar` leva o destino definido pelo
 * supervisor no rótulo ("Devolução para destinar: estoque" — `TagWireLabel`,
 * de `detail.destination` do wire ou, sem ele, de `info` = `{ destination }`);
 * sem destino, o rótulo do catálogo. O title diz quem define/o que falta.
 * Apoio e pendência do financeiro (2026-09-29): `apoio_financeiro_solicitado`
 * e `pendencia_financeira` põem o `detail` do wire (pedido do supervisor /
 * nota da pendência, quem e quando) no title — `TagSupportTitleParts`.
 */
export const TagChip = ({ tagKey = null, tag = null, size = 'sm', slaMinutes = null, info = null }) => {
    const { key, sector, kind, urgent, tone, label: catalogLabel, origin } = resolveTag(tagKey, tag);
    const classes = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
    const pad = size === 'md' ? 'px-2.5 py-1 text-[12px]' : 'px-2 py-0.5 text-[11px]';
    // Regras (rodada 3b + adendo SLA): prazo/SLA/atraso — `TagDeadlineParts`.
    const deadlineParts = tag && typeof tag === 'object' ? TagDeadlineParts(tag, { slaMinutes }) : [];
    const withdrawalParts = key === WITHDRAWAL_TAG_KEY ? WithdrawalTitleParts(tag?.detail || info) : [];
    const destinationSource = { key, detail: tag?.detail || info };
    const label = key === DESTINATION_TAG_KEY ? TagWireLabel(destinationSource) : catalogLabel;
    const title = [
        SECTOR_META[sector]?.label,
        kind ? TAG_KIND_META[kind]?.label.toLowerCase() : null,
        urgent ? 'urgente' : null,
        ...TagDestinationTitleParts(destinationSource),
        ...withdrawalParts,
        ...TagSupportTitleParts(tag, FormatShortInstant),
        ...deadlineParts,
    ]
        .filter(Boolean)
        .join(' · ');
    // `max-w-full` + rótulo `truncate`: um chip mais largo que a célula (rótulos
    // longos do catálogo v3 na coluna Tags) corta com reticências em vez de
    // vazar da tabela — o rótulo inteiro fica no `title`.
    return (
        <span
            title={title || undefined}
            className={`inline-flex max-w-full items-center gap-1 whitespace-nowrap font-semibold ${pad} ${classes.chip} ${
                urgent ? URGENT_RING_CLASSES : classes.ring
            }`}
        >
            {urgent ? <span className="h-1.5 w-1.5 shrink-0 bg-danger-600 dark:bg-danger-400" /> : null}
            <span className="min-w-0 truncate">{label}</span>
            <OriginBadge origin={origin} />
        </span>
    );
};

/** Chip de tag MANUAL: cor escolhida pelo operador; `onRemove` mostra o ×. */
export const ManualTagChip = ({ tag, onRemove = null, size = 'sm' }) => {
    const color = ManualTagColor(tag?.color);
    const pad = size === 'md' ? 'px-2.5 py-1 text-[12px]' : 'px-2 py-0.5 text-[11px]';
    return (
        <span className={`inline-flex items-center gap-1 whitespace-nowrap font-semibold ${pad} ${color.chip}`}>
            <span className={`h-1.5 w-1.5 shrink-0 ${color.swatch}`} />
            {tag?.name}
            <OriginBadge origin={tag?.origin} />
            {onRemove ? (
                <button
                    type="button"
                    aria-label={`Remover tag ${tag?.name || ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(tag);
                    }}
                    className="ml-0.5 opacity-60 transition-opacity hover:opacity-100"
                >
                    <X size={10} />
                </button>
            ) : null}
        </span>
    );
};

/** Pill da situação do pedido no ML — valor desconhecido aparece CRU. */
export const OrderStatusPill = ({ status }) => {
    const meta = ORDER_STATUS_META[status] || { label: status || '—', pill: PILL_NEUTRAL };
    return (
        <span
            className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.pill}`}
        >
            {meta.label}
        </span>
    );
};

// ── Identidade ───────────────────────────────────────────────────────────────

/** Altura da logo do canal por tamanho; a largura segue a proporção do asset. */
const CHANNEL_LOGO_HEIGHT = { sm: 'h-4', md: 'h-5', lg: 'h-6' };

/**
 * Logo do canal de venda (rodada 3). 'mercadolivre' → asset LOCAL de
 * `CHANNEL_META` (`variant` 'mark' = ícone, para a célula estreita da lista;
 * 'logo' = logo completa, para o cabeçalho do detalhe), com `alt` e `title`
 * = nome do canal. Canal sem asset (shopee/magalu, ou desconhecido) → texto
 * curto; sem canal → "—". Nunca hotlink.
 */
export const ChannelLogo = ({ channel, size = 'sm', variant = 'mark' }) => {
    const meta = ChannelMeta(CleanIdentityValue(channel));
    if (!meta) {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    const src = (variant === 'logo' ? meta.logo : meta.mark) || meta.mark || meta.logo;
    if (!src) {
        return (
            <span
                title={meta.label}
                className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-mono-600 dark:text-mono-300"
            >
                {meta.short}
            </span>
        );
    }
    return (
        <img
            src={src}
            alt={meta.label}
            title={meta.label}
            draggable={false}
            className={`${CHANNEL_LOGO_HEIGHT[size] || CHANNEL_LOGO_HEIGHT.sm} w-auto shrink-0 select-none`}
        />
    );
};

/** Ordem canônica dos 4 campos de identidade (decisão 6 da rodada 2). */
export const IDENTITY_FIELDS = ['canal', 'pedido', 'pack', 'bling'];

/**
 * Identidade multi-canal em 4 campos SEPARADOS: Canal / Pedido do canal /
 * Pack / Pedido Bling — "—" quando vazio, nunca 'null'. Negrito só no nº do
 * pedido (a manchete). O campo Canal mostra a LOGO do marketplace
 * (`ChannelLogo`, logo completa; texto curto quando o canal não tem asset).
 * Aceita o `order` do wire (`channel`, `channel_order_id`, `pack_id`,
 * `bling_order_id`) ou os valores soltos; os props antigos
 * (`externalOrderId`/`packId`) seguem valendo. `fields` limita quais campos
 * aparecem (ex.: `['pedido']` numa célula de lista).
 */
export const PedidoIdentity = ({
    order = null,
    externalOrderId = null,
    packId = null,
    channel = null,
    channelOrderId = null,
    blingOrderId = null,
    size = 'sm',
    fields = IDENTITY_FIELDS,
}) => {
    const values = {
        canal: CleanIdentityValue(channel ?? order?.channel),
        pedido: CleanIdentityValue(
            channelOrderId ?? order?.channel_order_id ?? externalOrderId ?? order?.external_order_id,
        ),
        pack: CleanPackId(packId ?? order?.pack_id),
        bling: CleanIdentityValue(blingOrderId ?? order?.bling_order_number ?? order?.bling_order_id),
    };
    const labels = { canal: 'Canal', pedido: 'Pedido do canal', pack: 'Pack', bling: 'Pedido Bling' };
    const main = size === 'md' ? 'text-base' : 'text-[13px]';
    const secondary = size === 'md' ? 'text-sm' : 'text-[12px]';
    const valueClass = {
        canal: `flex items-center ${secondary} text-mono-700 dark:text-mono-200`,
        pedido: `font-mono font-bold text-black dark:text-white ${main}`,
        pack: `font-mono ${secondary} text-sky-700 dark:text-sky-300`,
        bling: `font-mono ${secondary} text-mono-500 dark:text-mono-400`,
    };
    const list = (Array.isArray(fields) ? fields : IDENTITY_FIELDS).filter((key) => labels[key]);
    const renderValue = (key) => {
        if (key === 'canal') {
            return <ChannelLogo channel={values.canal} size={size} variant="logo" />;
        }
        if (key === 'pedido' && values.pedido) {
            return `#${values.pedido}`;
        }
        return values[key] || '—';
    };
    return (
        <dl className="flex min-w-0 flex-wrap items-end gap-x-5 gap-y-1.5">
            {list.map((key) => (
                <div key={key} className="min-w-0">
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">
                        {labels[key]}
                    </dt>
                    <dd className={`truncate leading-tight ${valueClass[key]}`}>{renderValue(key)}</dd>
                </div>
            ))}
        </dl>
    );
};

/**
 * Borda esquerda da linha pela prioridade: red se alguma tag URGENTE
 * (`urgent` do wire — keys soltas nunca são urgentes), senão a cor do setor
 * da PRIMEIRA tag (passe a lista por `SortTagsActionFirst` para a ação
 * mandar), senão neutra. Aceita objetos do wire ou só as keys.
 */
export function RowAccentClass(tags = []) {
    const list = Array.isArray(tags) ? tags : [];
    const resolved = list.map((t) => (typeof t === 'string' ? resolveTag(t, null) : resolveTag(null, t)));
    if (resolved.some((t) => t.urgent)) {
        return TONE_CLASSES.red.border;
    }
    const first = resolved[0];
    const tone = first ? SECTOR_META[first.sector]?.tone : null;
    return (tone && TONE_CLASSES[tone]?.border) || TONE_CLASSES.neutral.border;
}

// ── Ocorrências (pós-venda) — vocabulário compartilhado lista × detalhe ─────

/** Rótulo pt-BR do universo da ocorrência (`kind`/`kinds` do wire). */
export const OCCURRENCE_KIND_LABELS = {
    reclamacao: 'Reclamação',
    mediacao: 'Mediação',
    devolucao: 'Devolução',
};

/**
 * Título da tela do caso: "Reclamação #x" / "Mediação #x" / "Devolução #x";
 * sem claim do ML, cai no fim do id interno. O universo que NOMEIA o caso é
 * o primeiro de `kinds` que não seja "devolução" (F05: mediação/reclamação
 * com devolução física continua "Mediação #x"/"Reclamação #x" — a devolução
 * é o chip); devolução pura segue "Devolução #x". Sem `kinds`, vale `kind`.
 */
export function OccurrenceTitle(occ) {
    const kinds = Array.isArray(occ?.kinds) ? occ.kinds : [];
    const kind = kinds.find((item) => item !== 'devolucao') || occ?.kind || kinds[0] || null;
    const kindLabel = OCCURRENCE_KIND_LABELS[kind] || 'Ocorrência';
    const claim = CleanIdentityValue(occ?.external_claim_id);
    const tail = claim || (occ?.id ? String(occ.id).slice(-8) : '—');
    return `${kindLabel} #${tail}`;
}

// ── Atalhos externos (decisão 5 da rodada 2) ─────────────────────────────────

/**
 * Venda no painel do vendedor do ML. Pedido dentro de um pack: o painel do ML
 * endereça pelo nº do PACK (com o nº do pedido a página falha — relato de
 * operador, 24/09).
 */
export function MlSaleUrl(orderId, packId = null) {
    const reference = CleanPackId(packId) || orderId;
    return `https://www.mercadolivre.com.br/vendas/${encodeURIComponent(reference)}/detalhe`;
}

/** Mensageria pós-venda no ML — pelo pack quando houver, senão pelo pedido. */
export function MlMessagesUrl(orderId, packId = null) {
    const reference = CleanPackId(packId) || orderId;
    return `https://vendedores.mercadolivre.com.br/vendas/novo/mensagens/${encodeURIComponent(reference)}`;
}

/** Reclamação/mediação no ML (a resposta é por lá quando a mensageria bloqueia) — pelo pack quando houver. */
export function MlClaimMessagesUrl(orderId, claimId, packId = null) {
    const reference = CleanPackId(packId) || orderId;
    return `https://vendedores.mercadolivre.com.br/vendas/novo/mensagens/${encodeURIComponent(
        reference,
    )}/mediacao/${encodeURIComponent(claimId)}`;
}

const SHORTCUT_LINK_CLASS =
    'inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-sky-700 underline-offset-2 hover:underline dark:text-sky-300';
const SHORTCUT_MUTED_CLASS =
    'inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-mono-400 dark:text-mono-600';

const ExternalShortcut = ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className={SHORTCUT_LINK_CLASS}>
        {children} <ArrowSquareOut size={12} />
    </a>
);

/**
 * Barra de atalhos SEMPRE visível sob o cabeçalho do detalhe, em todas as
 * abas: venda / mensagens / reclamação por caso / anúncio por item no ML;
 * Pagamentos (detalhe do pagamento), ocorrência aqui e pedido vinculado no Bling.
 * Externos abrem em nova aba (`target=_blank`, `rel=noreferrer`). O envio no
 * ML fica como campo preparado (sem link direto conhecido no painel).
 */
export const ShortcutsBar = ({ order, ocorrencias = [] }) => {
    const orderId = CleanIdentityValue(order?.channel_order_id ?? order?.external_order_id);
    if (!orderId) {
        return null;
    }
    const packId = CleanPackId(order?.pack_id);
    const shipmentId = CleanIdentityValue(order?.shipping?.shipment_id);
    const blingOrderId = CleanIdentityValue(order?.bling_order_id);
    const itemIds = [
        ...new Set((Array.isArray(order?.items) ? order.items : []).map((item) => CleanIdentityValue(item?.item_id))),
    ].filter(Boolean);
    const cases = (Array.isArray(ocorrencias) ? ocorrencias : []).filter(
        (occ) => occ && (occ.id || occ.external_claim_id),
    );

    const groupLabel = (text) => (
        <span className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">{text}</span>
    );

    return (
        <nav
            aria-label="Atalhos do pedido"
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border border-mono-200 bg-white px-4 py-2 dark:border-mono-800 dark:bg-mono-950"
        >
            {groupLabel('Mercado Livre')}
            <ExternalShortcut href={MlSaleUrl(orderId, packId)}>Venda</ExternalShortcut>
            <ExternalShortcut href={MlMessagesUrl(orderId, packId)}>Mensagens</ExternalShortcut>
            {cases
                .filter((occ) => CleanIdentityValue(occ.external_claim_id))
                .map((occ) => (
                    <ExternalShortcut
                        key={`ml-${occ.id || occ.external_claim_id}`}
                        href={MlClaimMessagesUrl(orderId, occ.external_claim_id, packId)}
                    >
                        {OccurrenceTitle(occ)}
                    </ExternalShortcut>
                ))}
            {itemIds.map((itemId) => (
                <ExternalShortcut key={`item-${itemId}`} href={MLB_URLS.search(itemId)}>
                    Anúncio <span className="font-mono">{itemId}</span>
                </ExternalShortcut>
            ))}
            <span className={SHORTCUT_MUTED_CLASS} title="Sem link direto do envio no painel do ML">
                Envio <span className="font-mono">{shipmentId || '—'}</span>
            </span>

            <span aria-hidden="true" className="h-4 w-px bg-mono-200 dark:bg-mono-800" />
            {groupLabel('Melidash')}
            <Link to={`/payments/order/${encodeURIComponent(orderId)}`} className={SHORTCUT_LINK_CLASS}>
                Pagamentos
            </Link>
            {cases
                .filter((occ) => occ.id)
                .map((occ) => (
                    <Link
                        key={`rma-${occ.id}`}
                        to={`/rmas/${encodeURIComponent(occ.id)}`}
                        className={SHORTCUT_LINK_CLASS}
                    >
                        {OccurrenceTitle(occ)} (tela antiga)
                    </Link>
                ))}

            <span aria-hidden="true" className="h-4 w-px bg-mono-200 dark:bg-mono-800" />
            {groupLabel('Bling')}
            {blingOrderId ? (
                <ExternalShortcut href={`https://www.bling.com.br/vendas.php#edit/${encodeURIComponent(blingOrderId)}`}>
                    Pedido <span className="font-mono">{order.bling_order_number || blingOrderId}</span>
                </ExternalShortcut>
            ) : (
                <span className={SHORTCUT_MUTED_CLASS}>Pedido sem vínculo</span>
            )}
        </nav>
    );
};

// ── Menu de blocos (Todos | Atendimento | Logística | Financeiro) ────────────

const BIG_NUMBER_CLASS = 'text-xl font-bold leading-none tabular-nums';
const URGENT_COUNT_CLASS =
    'inline-flex items-center gap-1 text-[12px] font-semibold leading-none tabular-nums text-danger-600 dark:text-danger-400';
const SMALL_LINE_CLASS = 'truncate text-[11px] tabular-nums text-mono-400 dark:text-mono-500';

/**
 * Explicação dos números do bloco (title): pendências, urgentes e total — o
 * trecho dos urgentes é o MESMO `UrgentRuleTitle` do chip "Urgentes".
 */
function sectorBlockTitle(summary, sector) {
    const scope = sector ? 'deste setor' : 'de qualquer setor';
    return [
        `Número grande — pendências: pedidos com pelo menos uma ação nossa ${scope} (tags "Para fazer").`,
        `Em vermelho — ${UrgentRuleTitle(summary?.urgency_cutoff)}.`,
        sector
            ? 'No total: pedidos com qualquer tag deste setor (inclui "Acompanhar").'
            : 'No total: todos os pedidos da busca/período.',
    ].join('\n');
}

/**
 * Números do bloco (decisão do Thiago, rodada 5 — "não tem que ser urgente,
 * tem que ser de pendências; pode ter ambos"): o número GRANDE é o de
 * PENDÊNCIAS ("para fazer": pedidos com ≥1 tag de ação), em preto/branco
 * (cinza quando 0); ao lado, um contador VERMELHO menor "N urgentes" só quando
 * > 0 (ação com prazo do ML até o corte da Regra 1 OU que passou do SLA);
 * embaixo, pequeno e cinza, "T no total".
 *   setor → `sectors.X.acao` / `sectors.X.urgent` / `sectors.X.total`;
 *   Todos (`sector` null) → `acao_total` (fallback `pending_total`) /
 *           `urgent_total` / `total`.
 * Sem summary ainda, "…"; campo ausente vira "—" (nunca soma no front).
 */
export const SectorBlockNumbers = ({ summary = null, sector = null }) => {
    if (!summary) {
        return (
            <>
                <span className={`${BIG_NUMBER_CLASS} text-mono-300 dark:text-mono-700`}>…</span>
                <span className={SMALL_LINE_CLASS}>carregando…</span>
            </>
        );
    }
    const counts = sector ? summary.sectors?.[sector] || null : null;
    const urgent = sector ? counts?.urgent : summary.urgent_total;
    const action = sector ? counts?.acao : (summary.acao_total ?? summary.pending_total);
    const total = sector ? counts?.total : summary.total;
    const has = (value) => value !== null && value !== undefined;
    const actionNum = has(action) ? Number(action) || 0 : null;
    const urgentNum = has(urgent) ? Number(urgent) || 0 : 0;
    const bigTone =
        actionNum === null
            ? 'text-mono-300 dark:text-mono-700'
            : actionNum > 0
              ? 'text-black dark:text-white'
              : 'text-mono-400 dark:text-mono-500';
    return (
        <span className="flex min-w-0 flex-col gap-1" title={sectorBlockTitle(summary, sector)}>
            <span className="flex min-w-0 items-baseline gap-2">
                <span className={`${BIG_NUMBER_CLASS} ${bigTone}`}>
                    {actionNum === null ? '—' : FormatInt(actionNum)}
                </span>
                {urgentNum > 0 ? (
                    <span className={URGENT_COUNT_CLASS}>
                        <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 bg-danger-600 dark:bg-danger-400" />
                        {FormatInt(urgentNum)} {urgentNum === 1 ? 'urgente' : 'urgentes'}
                    </span>
                ) : null}
            </span>
            <span className={SMALL_LINE_CLASS}>{has(total) ? `${FormatInt(Number(total) || 0)} no total` : '—'}</span>
        </span>
    );
};

/**
 * Mesmo padrão do `menuTabs` de `pages/Rmas.jsx`: grid `gap-px`, bloco ativo
 * com borda superior preta 3px, ícone + rótulo CAPS + números do bloco
 * (`SectorBlockNumbers`: pendências em grande, "N urgentes" em vermelho ao
 * lado, total embaixo).
 * `active` = '' (Todos) ou setor.
 */
export const SectorBlocks = ({ summary = null, active = '', onChange }) => {
    const blocks = [
        { key: '', label: 'Todos', Icon: ClipboardText, sector: null },
        ...SECTOR_ORDER.map((sector) => ({
            key: sector,
            label: SECTOR_META[sector].label,
            Icon: SECTOR_META[sector].Icon,
            sector,
        })),
    ];
    return (
        <div className="grid grid-cols-2 gap-px border border-mono-200 bg-mono-200 dark:border-mono-800 dark:bg-mono-800 sm:grid-cols-4">
            {blocks.map((block) => {
                const isActive = (active || '') === block.key;
                const BlockIcon = block.Icon;
                return (
                    <button
                        key={block.key || 'todos'}
                        type="button"
                        onClick={() => onChange?.(block.key)}
                        aria-pressed={isActive}
                        className={`flex flex-col gap-1 border-t-[3px] px-4 py-2.5 text-left transition-colors ${
                            isActive
                                ? 'border-black bg-white dark:border-white dark:bg-mono-950'
                                : 'border-transparent bg-white hover:bg-mono-50 dark:bg-mono-950 dark:hover:bg-mono-900'
                        }`}
                    >
                        <span
                            className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${
                                isActive ? 'text-black dark:text-white' : 'text-mono-400 dark:text-mono-600'
                            }`}
                        >
                            <BlockIcon size={13} /> {block.label}
                        </span>
                        <SectorBlockNumbers summary={summary} sector={block.sector} />
                    </button>
                );
            })}
        </div>
    );
};

// ── Editor de tags manuais ───────────────────────────────────────────────────

/** Mensagem pt-BR para a falha de criação de tag (409 nome duplicado, 400…). */
function createTagErrorMessage(err) {
    if (err?.code === 'TAG_NAME_TAKEN' || err?.status === 409) {
        return 'Já existe uma tag com esse nome.';
    }
    return err?.message || 'Não foi possível criar a tag.';
}

/**
 * Lista das definições da org com marcação + "Nova tag" (nome + cor da
 * paleta). `selected` aceita ids ou objetos `{ id }`. `onToggle(def, next)`
 * recebe o estado DESEJADO; `onCreate({ name, color })` pode devolver promise —
 * se ela REJEITAR (ou resolver com `false`), o formulário fica aberto, mantém
 * o texto e mostra o erro. Sem `canEdit` mostra só as selecionadas (leitura).
 */
export const ManualTagPicker = ({
    definitions = [],
    selected = [],
    onToggle = null,
    onCreate = null,
    canEdit = false,
    busy = false,
}) => {
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');
    const [color, setColor] = useState('gray');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const selectedIds = new Set(
        (selected || []).map((item) => String(item && typeof item === 'object' ? item.id : item)),
    );

    const closeForm = () => {
        setCreating(false);
        setName('');
        setColor('gray');
        setError('');
    };

    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed || !onCreate || saving) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            const result = await onCreate({ name: trimmed, color });
            if (result === false) {
                setError('Não foi possível criar a tag.');
                return;
            }
            closeForm();
        } catch (err) {
            setError(createTagErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    if (!canEdit) {
        const chosen = (definitions || []).filter((def) => selectedIds.has(String(def.id)));
        if (!chosen.length) {
            return <span className="text-[11px] text-mono-400 dark:text-mono-600">sem tags manuais</span>;
        }
        return (
            <div className="flex flex-wrap items-center gap-1">
                {chosen.map((def) => (
                    <ManualTagChip key={def.id} tag={def} />
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-1">
                {(definitions || []).map((def) => {
                    const on = selectedIds.has(String(def.id));
                    const colorMeta = ManualTagColor(def.color);
                    return (
                        <button
                            key={def.id}
                            type="button"
                            disabled={busy}
                            aria-pressed={on}
                            onClick={() => onToggle?.(def, !on)}
                            className={`inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                                on
                                    ? colorMeta.chip
                                    : 'bg-white text-mono-500 ring-1 ring-inset ring-mono-300 hover:ring-mono-500 dark:bg-mono-950 dark:text-mono-400 dark:ring-mono-700 dark:hover:ring-mono-500'
                            }`}
                        >
                            <span className={`h-1.5 w-1.5 shrink-0 ${colorMeta.swatch}`} />
                            {def.name}
                            {on ? <Check size={11} /> : null}
                        </button>
                    );
                })}
                {onCreate && !creating ? (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => setCreating(true)}
                        className="inline-flex items-center gap-1 border border-dashed border-mono-300 px-2 py-0.5 text-[11px] font-semibold text-mono-500 transition-colors hover:border-black hover:text-black disabled:opacity-50 dark:border-mono-700 dark:text-mono-400 dark:hover:border-white dark:hover:text-white"
                    >
                        <Plus size={11} /> Nova tag
                    </button>
                ) : null}
                {!definitions?.length && !creating ? (
                    <span className="text-[11px] text-mono-400 dark:text-mono-600">nenhuma tag manual criada</span>
                ) : null}
            </div>
            {creating ? (
                <div className="flex flex-col gap-1.5 border border-mono-200 bg-mono-50 p-2 dark:border-mono-800 dark:bg-mono-900/40">
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            // biome-ignore lint/a11y/noAutofocus: formulário aberto por clique explícito do operador
                            autoFocus
                            value={name}
                            maxLength={60}
                            placeholder="Nome da tag"
                            aria-invalid={error ? true : undefined}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (error) {
                                    setError('');
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    submit();
                                } else if (e.key === 'Escape') {
                                    closeForm();
                                }
                            }}
                            className={`w-44 border bg-white px-2 py-1 text-sm outline-none transition-colors focus:border-black dark:bg-mono-950 dark:focus:border-white ${
                                error
                                    ? 'border-danger-600 dark:border-danger-400'
                                    : 'border-mono-300 dark:border-mono-700'
                            }`}
                        />
                        <div className="flex items-center gap-1">
                            {MANUAL_TAG_COLOR_KEYS.map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    aria-pressed={color === key}
                                    aria-label={MANUAL_TAG_COLORS[key].label}
                                    title={MANUAL_TAG_COLORS[key].label}
                                    onClick={() => setColor(key)}
                                    className={`h-5 w-5 ${MANUAL_TAG_COLORS[key].swatch} ${
                                        color === key
                                            ? 'ring-2 ring-black ring-offset-1 ring-offset-white dark:ring-white dark:ring-offset-mono-950'
                                            : 'opacity-60 hover:opacity-100'
                                    }`}
                                />
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={saving || !name.trim()}
                            className="bg-black px-3 py-1 text-sm font-semibold text-white transition-colors hover:bg-mono-800 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-mono-100"
                        >
                            {saving ? 'Criando…' : 'Criar'}
                        </button>
                        <button
                            type="button"
                            onClick={closeForm}
                            className="px-2 py-1 text-sm text-mono-500 transition-colors hover:text-black dark:hover:text-white"
                        >
                            Cancelar
                        </button>
                    </div>
                    {error ? (
                        <p role="alert" className="text-[11px] text-danger-600 dark:text-danger-400">
                            {error}
                        </p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ── Linha do tempo (order_history + rma_events) ──────────────────────────────

/**
 * Rótulos dos eventos: os do pedido (`order_history.event_type`) e os da
 * ocorrência (`rma_events.type`, vocabulário do módulo RMA). Tipo fora do
 * mapa aparece cru.
 */
export const HISTORY_EVENT_LABELS = {
    tag_added: 'Tag aplicada',
    tag_removed: 'Tag removida',
    note: 'Nota',
    CRIACAO: 'Ocorrência criada',
    STATUS: 'Mudança de status',
    NOTA: 'Nota na ocorrência',
    ANEXO: 'Anexo',
    MEDIACAO: 'Mediação',
    REEMBOLSO: 'Reembolso',
    FISCAL: 'Fiscal',
    SYNC: 'Sincronização com o ML',
    BLING: 'Bling',
    ETIQUETA: 'Etiqueta de devolução',
    FORNECEDOR: 'Fabricante',
    DESTINO: 'Destino da devolução',
    // Rodada 5 (2026-09-29): triagem, decisão do supervisor, apoio do
    // financeiro (rma_events) e status do financeiro do pedido (order_history).
    TRIAGEM: 'Triagem concluída',
    ATENDIMENTO: 'Atendimento finalizado',
    REVISAO: 'Decisão do supervisor',
    APOIO_FIN: 'Apoio do financeiro',
    finance_review: 'Status do financeiro',
};

const HISTORY_ORIGIN = {
    pedido: {
        label: 'Pedido',
        border: 'border-l-black dark:border-l-white',
        text: 'text-black dark:text-white',
    },
    ocorrencia: {
        label: 'Ocorrência',
        border: TONE_CLASSES.sky.border,
        text: TONE_CLASSES.sky.text,
    },
};

export function HistoryEventLabel(event) {
    const type = event?.event_type;
    return HISTORY_EVENT_LABELS[type] || String(type || 'Evento');
}

/**
 * Eventos mesclados do endpoint `/pedidos/:id/history` (já ordenados desc):
 * `{ id, source: 'pedido'|'ocorrencia', rma_id, event_type, payload, note,
 *    created_by_name, created_at }`. Cor pela ORIGEM (borda esquerda). O
 * `payload` chega como OBJETO: as tags trazem `{ tag_id, tag_name, tag_color }`
 * (contrato da rodada 2 — sem parse de string, sem nomes alternativos);
 * transições da ocorrência trazem `from_value` → `to_value`.
 */
export const HistoryTimeline = ({ events = [], emptyText = 'Sem eventos neste pedido.' }) => {
    const list = Array.isArray(events) ? events : [];
    if (!list.length) {
        return <p className="py-4 text-center text-xs text-mono-500 dark:text-mono-400">{emptyText}</p>;
    }
    return (
        <div className="flex flex-col">
            {list.map((event, i) => {
                const origin = HISTORY_ORIGIN[event.source] || HISTORY_ORIGIN.pedido;
                const payload = event.payload && typeof event.payload === 'object' ? event.payload : {};
                const tagName = payload.tag_name || null;
                const tagColor = payload.tag_color ? ManualTagColor(payload.tag_color) : null;
                const transition =
                    payload.from_value || payload.to_value
                        ? `${payload.from_value ? `${payload.from_value} → ` : ''}${payload.to_value || ''}`
                        : null;
                const claim = payload.external_claim_id ? `#${payload.external_claim_id}` : null;
                return (
                    <div
                        key={event.id || `${event.source}-${i}`}
                        className={`flex gap-3 border-l-4 py-2.5 pl-3 ${origin.border} ${
                            i > 0 ? 'border-t border-t-mono-100 dark:border-t-mono-900' : ''
                        }`}
                    >
                        <div className="w-28 shrink-0 pt-0.5">
                            <p className="text-[11px] font-medium text-mono-500" title={timeAgo(event.created_at)}>
                                {fmtDate(event.created_at)}
                            </p>
                            <p className="truncate text-[11px] text-mono-400">{event.created_by_name || 'sistema'}</p>
                        </div>
                        <div className="min-w-0 flex-1 text-sm">
                            <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className={`text-[10px] font-bold uppercase tracking-widest ${origin.text}`}>
                                    {origin.label}
                                    {claim ? (
                                        <span className="ml-1 font-mono normal-case tracking-normal">{claim}</span>
                                    ) : null}
                                </span>
                                <span className="font-medium text-black dark:text-white">
                                    {HistoryEventLabel(event)}
                                </span>
                                {tagName ? (
                                    <span
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold ${
                                            tagColor ? tagColor.chip : TONE_CLASSES.neutral.chip
                                        }`}
                                    >
                                        {tagColor ? <span className={`h-1.5 w-1.5 ${tagColor.swatch}`} /> : null}
                                        {tagName}
                                    </span>
                                ) : null}
                            </p>
                            {transition ? <p className="text-mono-700 dark:text-mono-200">{transition}</p> : null}
                            {event.note ? (
                                <p className="whitespace-pre-wrap text-mono-600 dark:text-mono-300">{event.note}</p>
                            ) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ── NF-e de devolução e NF de perda do caso (rodada 7 — spec-nf-xml) ────────
// Marcar "emitida" SUBINDO O XML da NF-e (PROVISÓRIO até a integração com o
// Bling/acervo de XML — a chave de acesso é o identificador). Usado na aba
// Financeiro do pedido e no caso embutido (com formulário, travado por
// rmas:finance:update) e, só para EXIBIR número/chave/XML, na tela antiga
// `/rmas/:id` (que continua marcando sem arquivo).

/**
 * DANFE (PDF) da NF-e de devolução emitida pelo ML — token por query para
 * abrir em nova aba, mesmo padrão de `/rmas/:id/invoice/danfe` (NF da venda).
 */
export function ReturnInvoiceDanfeUrl(rmaId) {
    const token = GetToken();
    return `${ResolveUploadsBaseUrl()}/rmas/${encodeURIComponent(rmaId)}/return-invoice/danfe${
        token ? `?access_token=${token}` : ''
    }`;
}

/** `warning` do wire (texto ou `{ message }`) → texto; nada → null. */
export function FiscalWarningText(warning) {
    if (!warning) {
        return null;
    }
    if (typeof warning === 'string') {
        return warning;
    }
    return warning.message ? String(warning.message) : null;
}

const FISCAL_LINK_CLASS =
    'text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:text-mono-400 dark:hover:text-white';

/**
 * Situação da NF do caso, SÓ LEITURA: NF-e de devolução emitida pelo ML
 * ("emitida pelo ML nº X" + DANFE) ou NF marcada por nós ("nº X · chave … ·
 * baixar XML"; marcada sem XML na tela antiga → "marcada em dd/mm · sem
 * XML"). Sem nada disso devolve `fallback` (padrão null). `extra` = número
 * e chave que o upload acabou de ler, enquanto o pedido recarrega.
 */
export const CaseFiscalDocText = ({ occ, type, extra = null, fallback = null }) => {
    const meta = CASE_FISCAL_TYPES[type];
    const doc = CaseFiscalDoc(occ, type);
    if (!meta || !doc) {
        return fallback;
    }
    if (type === 'ENTRADA' && HasMlReturnInvoice(occ)) {
        return (
            <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-mono-700 dark:text-mono-200">
                    <span>
                        NF-e de devolução emitida pelo ML
                        {occ.ml_return_invoice_number ? (
                            <>
                                {' '}
                                nº{' '}
                                <span className="font-mono text-black dark:text-white">
                                    {occ.ml_return_invoice_number}
                                </span>
                            </>
                        ) : null}
                    </span>
                    <a
                        href={ReturnInvoiceDanfeUrl(occ.id)}
                        target="_blank"
                        rel="noreferrer"
                        className={FISCAL_LINK_CLASS}
                    >
                        baixar DANFE
                    </a>
                </span>
                {occ.ml_return_invoice_key ? (
                    <span className="break-all font-mono text-[10px] text-mono-400 dark:text-mono-500">
                        chave {occ.ml_return_invoice_key}
                    </span>
                ) : null}
            </span>
        );
    }
    const number = doc.number || extra?.number || null;
    const key = doc.key || extra?.key || null;
    if (!doc.at && !number) {
        return fallback;
    }
    const xmlUrl = doc.xml ? ResolveAssetUrl(doc.xml) : null;
    return (
        <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-mono-700 dark:text-mono-200">
                {number ? (
                    <span>
                        {meta.label} nº <span className="font-mono text-black dark:text-white">{number}</span>
                    </span>
                ) : (
                    <span>
                        {meta.label} marcada em {fmtDate(doc.at)}
                        <span className="text-mono-400 dark:text-mono-500"> · sem XML</span>
                    </span>
                )}
                {xmlUrl ? (
                    <a href={xmlUrl} target="_blank" rel="noreferrer" className={FISCAL_LINK_CLASS}>
                        baixar XML
                    </a>
                ) : null}
            </span>
            {key ? (
                <span className="break-all font-mono text-[10px] text-mono-400 dark:text-mono-500">chave {key}</span>
            ) : null}
        </span>
    );
};

/** Consulta e vínculo de nota já emitida no Bling, preservando os documentos históricos. */
export const CaseFiscalAction = (props) => (
    <CaseBlingFiscalAction
        {...props}
        renderDocument={(extra) => <CaseFiscalDocText occ={props.occ} type={props.type} extra={extra} />}
    />
);
